import { WarningIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { IterableElement } from 'type-fest';

import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { ITableHeader, Table } from '../../../../components/table/Table';
import { SaleStatus } from 'src/types/enums';
import { changeOrderSaleStatus, deleteOrderLineSale } from '../../models';
import { useUniqueArrayReducer } from '../../../../hooks/useUniqueArrayReducer';
import { getDisplayError } from '../../../../utils/get-display-error';
import { decimateAndFormatNumber } from '../../../../utils/number';
import { calculateLineTotal } from '../../utils/price';
import type { Order } from '../order/Order';
import { useOrderLine } from '../orderLine/orderLineContext';
import { useOrder } from '../order/orderContext';
import { LinkButton } from '../../../../components/button/ButtonLink';
import { useMinimalGeneralSettings } from '../../../../contexts/minimal-settings-context';
import { captureException } from '@sentry/react';
import { isDieselSurchargeProductType } from '../../../../domain/diesel-surcharge';

export type OrderLineSale = IterableElement<IterableElement<Order['lines']>['sales']>;

const ORDERLINE_HEADERS: ITableHeader[] = [
  {
    id: 'product',
    name: 'Artikel',
  },
  {
    id: 'amount',
    name: 'Hoeveelheid',
  },
  {
    id: 'unitPrice',
    name: 'Prijs',
  },
  {
    id: 'vatRate',
    name: 'Btw',
  },
  {
    id: 'total',
    name: 'Totaal',
  },
  {
    id: 'delete',
    name: '',
  },
];

export const OrderLineSalesPage = () => {
  const { settings } = useMinimalGeneralSettings();
  const { order, refreshData } = useOrder();
  const { orderLine } = useOrderLine();
  const [, mutatePendingDeletion] = useUniqueArrayReducer<number>();

  const sales: OrderLineSale[] = orderLine.sales;

  const lines: Array<OrderLineSale | 'total'> = useMemo(() => {
    return [...sales, 'total'];
  }, [sales]);

  const total = useMemo(() => {
    return sales.reduce((acc, item) => {
      return acc + calculateLineTotal(item);
    }, 0);
  }, [sales]);

  const hasDieselSurcharge = sales.some((p) =>
    isDieselSurchargeProductType(p.productType.id, settings.dieselSurchargeProductType?.id),
  );
  return (
    <div>
      <div className="flex justify-end mb-4">
        <div className="flex gap-2">
          {order.saleStatus === SaleStatus.Created && (
            <div>
              <ConfirmDialog
                triggerText="Markeer als onvolledig voor facturatie"
                title="Markeer als onvolledig?"
                submitText="Markeer als onvolledig"
                description={<div>Ben je zeker dat je dit order wilt markeren als onvolledig voor facturatie?</div>}
                onSubmit={async () => {
                  try {
                    await changeOrderSaleStatus(order.id, {
                      newStatus: SaleStatus.Incomplete,
                    });
                    toast.success('Order verkoopstatus aangepast');
                    await refreshData();
                  } catch (err) {
                    captureException(err);
                    toast.error('Kon order verkoopstatus niet aanpassen: ' + getDisplayError(err));
                  }
                }}
              />
            </div>
          )}
          {order.saleStatus === SaleStatus.Incomplete && (
            <div>
              <ConfirmDialog
                triggerText="Markeer als klaar voor facturatie"
                title="Markeer als klaar?"
                submitText="Markeer als klaar"
                description={<div>Ben je zeker dat je dit order wilt markeren als klaar voor facturatie?</div>}
                onSubmit={async () => {
                  try {
                    await changeOrderSaleStatus(order.id, {
                      newStatus: SaleStatus.Created,
                    });
                    toast.success('Order verkoopstatus aangepast');
                    await refreshData();
                  } catch (err) {
                    captureException(err);
                    toast.error('Kon order verkoopstatus niet aanpassen: ' + getDisplayError(err));
                  }
                }}
              />
            </div>
          )}
          <div>
            <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
              Voeg verkoop toe
            </LinkButton>
          </div>
        </div>
      </div>
      <Table
        idKey="id"
        headers={ORDERLINE_HEADERS}
        data={lines}
        mapData={(data) => {
          if (data === 'total') {
            return [
              <span className="font-medium">Totaal</span>,
              '',
              '',
              '',
              <span className="font-medium whitespace-nowrap">{`€ ${decimateAndFormatNumber(total, 2, {
                decimalSeperator: ',',
              })}`}</span>,
              '',
            ];
          } else {
            const deleteLine = async () => {
              mutatePendingDeletion({
                type: 'add',
                value: data.id,
              });
              try {
                await deleteOrderLineSale(data.id);
                toast.success('Order verkoop lijn gewist');
                await refreshData();
              } catch (err) {
                captureException(err);
                toast.error('Kon order verkoop lijn niet wissen: ' + getDisplayError(err));
              }
              mutatePendingDeletion({
                type: 'delete',
                value: data.id,
              });
            };

            return [
              <Link to={`${data.id}`} className="link-text">
                {data.productType.nameNl}
              </Link>,
              <div className="whitespace-nowrap">
                {`${decimateAndFormatNumber(data.amount, 2, {
                  decimalSeperator: ',',
                })}`}
              </div>,
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(data.unitPrice, 2, {
                decimalSeperator: ',',
              })}`}</div>,
              <div className="whitespace-nowrap">{`${decimateAndFormatNumber(data.vatRatePercentage, 2, {
                decimalSeperator: ',',
              })}%`}</div>,
              <div className="whitespace-nowrap">
                {`€ ${decimateAndFormatNumber(calculateLineTotal(data), 2, {
                  decimalSeperator: ',',
                })}`}
              </div>,
              <ConfirmDialog
                triggerColor="danger"
                triggerText={<TrashIcon className="button-icon" />}
                title="Verwijder verkoop"
                submitText="Verwijder"
                description={<div>{`Ben je zeker dat je deze verkoop ${data.productType.nameNl} wilt wissen?`}</div>}
                onSubmit={deleteLine}
              />,
            ];
          }
        }}
        isMultiSelect
      />

      {order.customer?.dieselSurchargeEnabled && !hasDieselSurcharge && (
        <div className="flex items-center gap-2 text-warning-text">
          <WarningIcon className="h-4 w-4" />
          <div>Diesel toeslag is nog niet toegekend.</div>
        </div>
      )}
    </div>
  );
};
